import type { CatalogueProductDetail, CatalogueRecipeVersion } from '@enp/domain/api';
import { formatNumber, formatPercent } from '@enp/domain/format';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  focusRing,
  PageHeader,
  Qty,
  Skeleton,
  StatusBadge,
  unitName,
  type Status,
} from '@enp/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  Archive,
  ArrowLeft,
  Boxes,
  Check,
  ClipboardCheck,
  PackageSearch,
  Pencil,
  Plus,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  deductionText,
  FAMILY_ROLE_LABEL,
  IDENTIFIER_KIND_LABEL,
  isMade,
  precisionWarningText,
  RECIPE_STATUS_LABEL,
  ROLE_LABEL,
  ROLE_ORDER,
  SERVICE_KIND_LABEL,
  stateLabel,
  stateStartLabel,
} from '../../../catalogue/labels.js';
import { productQuery } from '../../../catalogue/queries.js';
import { ApiError } from '../../../lib/api.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { AllergenChips, OwnedNote, useCan } from '../../../production/parts.js';
import { ProductEditDialog } from '../../../production/product-edit.js';
import { COST_SOURCE_LABEL, undeclaredAllergens, unitCostText } from '../../../production/words.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/catalogue/$productId')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.productId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(productQuery(params.productId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: ProductPage,
  pendingComponent: ProductSkeleton,
  errorComponent: ProductError,
  notFoundComponent: ProductNotFound,
});

type Product = CatalogueProductDetail;
type RecipeVersion = CatalogueRecipeVersion;

// ---------------------------------------------------------------- building blocks

function BackToCatalogue({ name }: { name: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/catalogue">Catalogue</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{name}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function Section({
  title,
  description,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  id: string;
}) {
  return (
    <Card aria-labelledby={`${id}-title`} role="region">
      <CardHeader>
        <CardTitle as="h2" id={`${id}-title`}>
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * Makes a horizontally scrolling box keyboard-reachable only while it actually scrolls
 * (axe scrollable-region-focusable), so wide screens get no extra tab stop.
 */
function useScrollFocusable(ref: RefObject<HTMLDivElement | null>) {
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setScrolls(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return scrolls;
}

/** A compact table inside a card: header row, hairlines, no outer border. */
function MiniTable({
  label,
  head,
  children,
}: {
  label: string;
  head: string[];
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const scrolls = useScrollFocusable(box);
  return (
    <div
      ref={box}
      className={cn('-mx-5 -mb-2 overflow-x-auto', focusRing)}
      {...(scrolls
        ? { tabIndex: 0, role: 'group', 'aria-label': `${label} (scrolls sideways)` }
        : {})}
    >
      <table aria-label={label} className="w-full border-collapse text-body">
        <thead>
          <tr className="border-y border-border bg-surface-sunken">
            {head.map((h, i) => (
              <th
                key={h}
                scope="col"
                className={`h-9 whitespace-nowrap px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted ${i === 0 ? 'pl-5' : ''} ${i === head.length - 1 ? 'pr-5' : ''}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        {/* The card's edge ends the table: no hairline under the last row. */}
        <tbody className="[&_td:first-child]:pl-5 [&_td:last-child]:pr-5 [&>tr:last-child>td]:border-b-0">
          {children}
        </tbody>
      </table>
    </div>
  );
}

const td = 'border-b border-border px-3 py-2 align-top';

function YesNo({ value, yes = 'Yes', no = 'No' }: { value: boolean; yes?: string; no?: string }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-fg">
      <Check aria-hidden className="size-4 text-state-success" />
      {yes}
    </span>
  ) : (
    <span className="text-muted">{no}</span>
  );
}

function None({ children = 'None' }: { children?: ReactNode }) {
  return <span className="text-muted">{children}</span>;
}

// ---------------------------------------------------------------- sections

/**
 * Best-before, from the product's whole expiry profile (UI1A-209): expiry states move
 * stock by its best-before date, so a product with states tracks it too.
 */
function bestBeforeText(product: Product): ReactNode {
  if (product.state_rule) return 'Tracked; it drives the expiry states';
  if (product.tracks_expiry) return 'Tracked on every batch or delivery';
  return <None>Not tracked</None>;
}

function Details({ product }: { product: Product }) {
  const archived = product.archived_at !== null;
  const owned = new Set(product.person_owned_fields ?? []);
  // A field a person changed here: imports ask before changing it (D-032).
  const mine = (field: string, value: ReactNode): ReactNode =>
    owned.has(field) ? (
      <span className="flex flex-col gap-0.5">
        <span>{value}</span>
        <OwnedNote />
      </span>
    ) : (
      value
    );
  const cost = product.standard_cost_cents ?? null;
  const rows: [string, ReactNode][] = [
    ['Category', product.category?.name ?? <None />],
    ['Stock unit', unitName(product.base_uom)],
    [
      'Shelf life',
      mine(
        'shelf_life_days',
        product.shelf_life_days === null ? (
          <None>Not set</None>
        ) : (
          `${product.shelf_life_days} ${product.shelf_life_days === 1 ? 'day' : 'days'}`
        ),
      ),
    ],
    ['Best-before', mine('tracks_expiry', bestBeforeText(product))],
    ['Used up', mine('deduction_mode', deductionText(product.deduction_mode, product.roles))],
    [
      'Standard cost',
      mine(
        'standard_cost_cents',
        <span className="flex flex-col gap-0.5">
          {cost === null ? <None>Not worked out</None> : unitCostText(cost, product.base_uom)}
          {product.cost_source && (
            <span className="text-xs text-muted">{COST_SOURCE_LABEL[product.cost_source]}</span>
          )}
          <Link
            to="/recipes/costs/$productId"
            params={{ productId: product.id }}
            className="text-sm font-medium text-primary-text underline-offset-4 hover:underline"
          >
            How it adds up
          </Link>
        </span>,
      ),
    ],
    ['VAT rate', product.vat_rate_code === 'standard' ? 'Standard' : product.vat_rate_code],
    ...(product.roles.includes('service')
      ? ([
          [
            'What it is',
            mine(
              'service_kind',
              product.service_kind ? (
                SERVICE_KIND_LABEL[product.service_kind]
              ) : (
                <None>Not stated (carries its VAT rate)</None>
              ),
            ),
          ],
        ] as [string, ReactNode][])
      : []),
    // The reason for a veto is in the Sales channels section; here, the fact only.
    [
      'Sales channels',
      archived
        ? 'Not offered (archived)'
        : product.channel_veto
          ? 'Not for sale'
          : 'May be offered on a channel',
    ],
    ['Last changed', <AccountDate key="updated" value={product.updated_at} withTime />],
  ];
  return (
    <Section id="details" title="Details">
      <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-2.5 text-body">
        {rows.map(([term, value]) => (
          <div key={term} className="contents">
            <dt className="text-muted">{term}</dt>
            <dd className="min-w-0 text-fg">{value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function Identifiers({ product }: { product: Product }) {
  if (product.identifiers.length === 0) {
    return (
      <Section id="codes" title="Codes">
        <p className="text-body text-muted">
          No codes yet. A SKU or barcode lets tills, labels and imports find this product.
        </p>
      </Section>
    );
  }
  return (
    <Section
      id="codes"
      title="Codes"
      description="How tills, labels, suppliers and imports recognise this product. Old codes stay on record."
    >
      <MiniTable label="Codes" head={['Kind', 'Code', 'Channel', 'From', 'Until']}>
        {product.identifiers.map((i) => (
          <tr key={i.id} className={i.valid_to ? 'text-muted' : undefined}>
            <td className={td}>{IDENTIFIER_KIND_LABEL[i.kind]}</td>
            <td className={`${td} break-all font-mono text-sm`}>
              {i.value}
              {i.canonical_value !== i.value && i.kind === 'sku' && (
                <span className="block font-body text-xs text-muted">
                  Matched as {i.canonical_value}
                </span>
              )}
            </td>
            <td className={td}>
              {i.channel_name ?? (i.channel_id ? 'A channel' : <None>Any</None>)}
            </td>
            <td className={td}>
              <AccountDate value={i.valid_from} />
            </td>
            <td className={td}>
              {i.valid_to ? (
                <AccountDate value={i.valid_to} />
              ) : (
                <Badge tone="success">Current</Badge>
              )}
            </td>
          </tr>
        ))}
      </MiniTable>
    </Section>
  );
}

function Family({ product }: { product: Product }) {
  const family = product.family;
  if (!family) return null;
  return (
    <Section
      id="family"
      title={`Family: ${family.name}`}
      description="Linked products, such as a whole cake and its slices. Archiving any of them shows every member first, so none is left behind by accident."
    >
      <ul className="flex flex-col gap-1.5">
        {family.members.map((m) => {
          const self = m.id === product.id;
          return (
            <li key={m.id} className="flex flex-wrap items-center gap-2">
              {self ? (
                <span className="font-medium text-fg" aria-current="page">
                  {m.name}
                </span>
              ) : (
                <Link
                  to="/catalogue/$productId"
                  params={{ productId: m.id }}
                  className="font-medium text-primary-text underline-offset-4 hover:underline"
                >
                  {m.name}
                </Link>
              )}
              <Badge tone="neutral">{FAMILY_ROLE_LABEL[m.family_role]}</Badge>
              {self && <span className="text-sm text-muted">(this product)</span>}
              {m.archived_at && (
                <Badge tone="outline">
                  <Archive aria-hidden />
                  Archived
                </Badge>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function channelNames(product: Product): Map<string, string> {
  const names = new Map<string, string>();
  for (const m of product.channel_mappings) names.set(m.channel.id, m.channel.name);
  for (const i of product.identifiers) {
    if (i.channel_id && i.channel_name) names.set(i.channel_id, i.channel_name);
  }
  return names;
}

/** "Shop till, Web shop"; a count when a channel's name isn't known on this page. */
function channelList(ids: string[], names: Map<string, string>): string {
  const known = ids.map((id) => names.get(id));
  if (known.every((n): n is string => n !== undefined)) return known.join(', ');
  return `${ids.length} ${ids.length === 1 ? 'channel' : 'channels'}`;
}

function StateRule({ product }: { product: Product }) {
  const rule = product.state_rule;
  if (!rule) {
    return (
      <Section id="states" title="Expiry states">
        <p className="text-body text-muted">
          {product.tracks_expiry
            ? 'Best-before dates are tracked, but no expiry states are set: stock stays sellable until someone removes it.'
            : 'This product doesn’t move through expiry states.'}
        </p>
      </Section>
    );
  }
  const names = channelNames(product);
  return (
    <Section
      id="states"
      title={`Expiry states: ${rule.name}`}
      description={
        rule.source === 'category'
          ? `From its category${product.category ? ` (${product.category.name})` : ''}. Stock moves to the next state as its best-before date comes closer.`
          : 'Set on this product. Stock moves to the next state as its best-before date comes closer.'
      }
    >
      <MiniTable
        label="Expiry states"
        head={['State', 'Starts', 'Sellable', 'Markdown', 'Channels', 'In recipes']}
      >
        {rule.states.map((s) => (
          <tr key={s.key}>
            <td className={`${td} font-medium`}>{stateLabel(s.key)}</td>
            <td className={td}>{stateStartLabel(s.fromDaysBeforeBestBefore)}</td>
            <td className={td}>
              <YesNo value={s.sellable} />
            </td>
            <td className={`${td} tabular-nums`}>
              {s.markdownBp > 0 ? `${formatPercent(s.markdownBp)} off` : <None />}
            </td>
            <td className={td}>
              {s.channels.length === 0 ? <None /> : channelList(s.channels, names)}
            </td>
            <td className={td}>
              <YesNo value={s.usableAsIngredient} yes="Usable" no="Not usable" />
            </td>
          </tr>
        ))}
      </MiniTable>
    </Section>
  );
}

const VERSION_STATUS: Record<RecipeVersion['status'], Status> = {
  active: 'success',
  draft: 'warning',
  retired: 'neutral',
};

function RecipeVersionBlock({ version }: { version: RecipeVersion }) {
  const warnings = version.lines.flatMap((l) => l.precision_warnings);
  const headingId = `recipe-v${version.version_no}`;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <h3 id={headingId} className="font-display text-base font-semibold text-fg">
          Version {version.version_no}
        </h3>
        <StatusBadge status={VERSION_STATUS[version.status]}>
          {RECIPE_STATUS_LABEL[version.status]}
        </StatusBadge>
        <span className="text-sm text-muted">
          Makes <Qty value={version.batch_yield_qty} unit={version.yield_uom} maxDecimals={6} /> per
          batch
        </span>
        {version.signed_off_at && (
          <span className="inline-flex items-center gap-1 text-sm text-muted">
            <ClipboardCheck aria-hidden className="size-4" />
            Signed off <AccountDate value={version.signed_off_at} />
          </span>
        )}
      </div>

      {version.status === 'draft' && (
        <div
          role="note"
          className="flex items-start gap-2.5 rounded-md border border-state-warning/40 bg-state-warning-subtle px-3 py-2 text-body text-fg"
        >
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-state-warning" />
          <p>
            <strong className="font-semibold">Waiting for the baker’s sign-off.</strong> A draft
            isn’t used for production until it’s signed off. Costs use the version in use, or the
            latest draft while none is signed off (so an unsigned cost may change).
            {warnings.length > 0 &&
              ` Check ${warnings.length === 1 ? 'the quantity' : `the ${warnings.length} quantities`} marked below first.`}
          </p>
        </div>
      )}
      {version.status !== 'draft' && version.signed_off_at && warnings.length > 0 && (
        <p className="flex items-center gap-2 text-sm text-fg">
          <TriangleAlert aria-hidden className="size-4 text-state-warning" />
          {warnings.length === 1
            ? '1 quantity was marked “Check” when the baker signed it off.'
            : `${warnings.length} quantities were marked “Check” when the baker signed it off.`}
        </p>
      )}

      {version.lines.length === 0 ? (
        <p className="text-body text-muted">No ingredients on this version yet.</p>
      ) : (
        <MiniTable
          label={`Ingredients, version ${version.version_no}`}
          head={['Ingredient', 'Quantity', 'Loss', 'States it may be in']}
        >
          {version.lines.map((l) => {
            const flagged = l.precision_warnings.length > 0;
            return [
              <tr key={l.id}>
                <td className={`${td} ${flagged ? 'border-b-0' : ''}`}>
                  <Link
                    to="/catalogue/$productId"
                    params={{ productId: l.component.id }}
                    className="font-medium text-primary-text underline-offset-4 hover:underline"
                  >
                    {l.component.name}
                  </Link>
                </td>
                <td className={`${td} whitespace-nowrap ${flagged ? 'border-b-0' : ''}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {flagged && <TriangleAlert aria-hidden className="size-4 text-state-warning" />}
                    <Qty value={l.qty} unit={l.uom} maxDecimals={6} />
                  </span>
                </td>
                <td className={`${td} tabular-nums ${flagged ? 'border-b-0' : ''}`}>
                  {Number(l.loss_pct) === 0 ? <None /> : `${formatNumber(l.loss_pct)}%`}
                </td>
                <td className={`${td} ${flagged ? 'border-b-0' : ''}`}>
                  {l.allowed_states.length === 0 ? (
                    <None>Any usable state</None>
                  ) : (
                    l.allowed_states.map(stateLabel).join(', ')
                  )}
                </td>
              </tr>,
              flagged && (
                <tr key={`${l.id}-warn`}>
                  <td colSpan={4} className={`${td} pt-0`}>
                    <ul className="flex flex-col gap-1">
                      {l.precision_warnings.map((w, i) => (
                        <li
                          key={i}
                          className="rounded-sm bg-state-warning-subtle px-2.5 py-1.5 text-sm text-fg"
                        >
                          <span className="font-semibold text-state-warning">Check: </span>
                          {precisionWarningText(w)}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ),
            ];
          })}
        </MiniTable>
      )}
      {version.instructions && (
        <p className="text-body text-fg">
          <span className="font-medium">Method: </span>
          {version.instructions}
        </p>
      )}
    </section>
  );
}

function Recipes({ product }: { product: Product }) {
  const recipes = product.recipes.filter((r) => !r.archived_at);
  const canEdit = useCan('recipe.edit', true);
  if (recipes.length === 0) {
    const writable = !product.archived_at && isMade(product.roles);
    return (
      <Section id="recipe" title="Recipe">
        <p className="text-body text-muted">
          {product.archived_at
            ? isMade(product.roles)
              ? 'No recipe on record. It’s archived, so it isn’t made any more.'
              : 'Bought in, so it has no recipe.'
            : isMade(product.roles)
              ? 'No recipe yet. The catalogue import brings the recipe in as a draft for the baker to sign off, or write one here.'
              : 'Bought in, so it has no recipe.'}
        </p>
        {writable && canEdit && (
          <Button variant="secondary" size="sm" className="mt-3" asChild>
            <Link to="/recipes/new" search={{ product: product.id }}>
              <Plus aria-hidden />
              Write a recipe
            </Link>
          </Button>
        )}
      </Section>
    );
  }
  return (
    <Section
      id="recipe"
      title="Recipe"
      description={
        <>
          Per batch, at full precision. One version is in use at a time.{' '}
          {recipes.map((r) => (
            <Link
              key={r.id}
              to="/recipes/$recipeId"
              params={{ recipeId: r.id }}
              className="font-medium text-primary-text underline-offset-4 hover:underline"
            >
              Open the recipe to edit or sign it off
            </Link>
          ))}
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {recipes.map((r) => {
          const current = r.versions.filter((v) => v.status !== 'retired');
          const retired = r.versions.filter((v) => v.status === 'retired');
          return (
            <div key={r.id} className="flex flex-col gap-6">
              {current.map((v) => (
                <RecipeVersionBlock key={v.id} version={v} />
              ))}
              {retired.length > 0 && (
                <details className="rounded-md border border-border px-4 py-2">
                  <summary className="cursor-pointer text-sm font-medium text-muted">
                    {retired.length === 1
                      ? '1 retired version'
                      : `${retired.length} retired versions`}
                  </summary>
                  <div className="flex flex-col gap-6 pt-3">
                    {retired.map((v) => (
                      <RecipeVersionBlock key={v.id} version={v} />
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

const MAPPING_STATUS: Record<
  Product['channel_mappings'][number]['status'],
  { status: Status; label: string }
> = {
  active: { status: 'success', label: 'Active' },
  review: { status: 'warning', label: 'Needs review' },
  vetoed: { status: 'neutral', label: 'Vetoed' },
};

function ChannelMappings({ product }: { product: Product }) {
  const mappings = product.channel_mappings.filter((m) => !m.archived_at);
  if (mappings.length === 0) {
    return (
      <Section id="channels" title="Sales channels">
        <p className="text-body text-muted">
          {product.archived_at
            ? 'Not linked to a sales channel. It’s archived, so it isn’t sold any more.'
            : product.channel_veto
              ? product.channel_veto.reason
              : product.roles.includes('sellable') || product.roles.includes('service')
                ? 'Not linked to a sales channel yet. The catalogue import links it to your till’s item.'
                : 'Not sold, so it isn’t linked to a sales channel.'}
        </p>
      </Section>
    );
  }
  const identifiers = new Map(product.identifiers.map((i) => [i.id, i]));
  return (
    <Section
      id="channels"
      title="Sales channels"
      description="Which item on each channel this product (in each state) is sold as, and how the link was made."
    >
      <MiniTable
        label="Sales channels"
        head={['Channel and state', 'Their item', 'Linked by', 'Status']}
      >
        {mappings.map((m) => {
          const matched = m.matched_by_identifier_id
            ? identifiers.get(m.matched_by_identifier_id)
            : undefined;
          const status = MAPPING_STATUS[m.status];
          return (
            <tr key={m.id}>
              <td className={`${td} whitespace-nowrap`}>
                <span className="block font-medium">{m.channel.name}</span>
                <span className="block text-sm text-muted">
                  {m.state ? stateLabel(m.state) : 'Any sellable state'}
                </span>
              </td>
              <td className={`${td} whitespace-nowrap font-mono text-sm`}>
                {m.external_variant_id ?? m.external_item_id}
                {m.external_variant_id && (
                  <span className="block font-body text-xs text-muted">
                    Item {m.external_item_id}
                  </span>
                )}
              </td>
              <td className={td}>
                {m.match_method === 'manual' ? (
                  'By hand'
                ) : matched ? (
                  <>
                    <span className="block text-sm text-muted">
                      {IDENTIFIER_KIND_LABEL[matched.kind]}
                    </span>
                    <span className="whitespace-nowrap font-mono text-sm">{matched.value}</span>
                  </>
                ) : (
                  'A code'
                )}
              </td>
              <td className={td}>
                <StatusBadge status={status.status}>{status.label}</StatusBadge>
                {m.status_reason && (
                  <span className="mt-1 block text-sm text-muted">{m.status_reason}</span>
                )}
                {m.status === 'review' && (
                  <Link
                    to="/imports"
                    className="mt-1 block text-sm font-medium text-primary-text underline-offset-4 hover:underline"
                  >
                    Check it in Imports
                  </Link>
                )}
              </td>
            </tr>
          );
        })}
      </MiniTable>
    </Section>
  );
}

function Allergens({ product, onEdit }: { product: Product; onEdit?: () => void }) {
  const active = product.recipes
    .flatMap((r) => r.versions)
    .find((v) => v.status === 'active' && v.signed_off_at);
  const owned = new Set(product.person_owned_fields ?? []);
  const declared = product.allergens ?? [];
  // What the recipe's ingredients contain (UI8A-1): never "None declared" above a statement
  // or ingredients that name allergens, as on the recipe page (UI5A-1).
  const ingredients = product.ingredient_allergens ?? [];
  const missing = undeclaredAllergens(declared, ingredients);
  const unticked = declared.length === 0 && missing.length === 0 && product.allergen_statement;
  return (
    <Section id="allergens" title="Allergens">
      <div className="mb-3 flex flex-col gap-1">
        {unticked ? (
          <p className="text-body text-fg">Not ticked on the product. The statement says:</p>
        ) : (
          <AllergenChips allergens={declared} ingredients={ingredients} />
        )}
        {missing.length > 0 && onEdit && (
          <Button variant="link" size="sm" className="self-start" onClick={onEdit}>
            Edit the allergens…
          </Button>
        )}
        {owned.has('allergens') && <OwnedNote />}
      </div>
      {product.allergen_statement ? (
        <div className="flex flex-col gap-2">
          <p className="text-body text-fg">{product.allergen_statement}</p>
          {active?.signed_off_at && (
            <p className="text-sm text-muted">
              Signed off with recipe version {active.version_no},{' '}
              <AccountDate value={active.signed_off_at} />.
            </p>
          )}
        </div>
      ) : (
        <p className="text-body text-muted">
          {product.archived_at
            ? 'No allergen statement on record.'
            : isMade(product.roles)
              ? 'No allergen statement yet. It comes from the recipe and is signed off by the baker.'
              : 'No allergen statement yet. For a bought-in product it comes from the supplier’s specification.'}
        </p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- page

function ProductPage() {
  const { productId } = Route.useParams();
  const { data: product } = useSuspenseQuery(productQuery(productId));
  useDocumentTitle(product.name);
  const roles = ROLE_ORDER.filter((r) => product.roles.includes(r));
  const canEdit = useCan('catalogue.edit', true);
  const [editing, setEditing] = useState(false);
  return (
    <>
      <PageHeader
        eyebrow={<BackToCatalogue name={product.name} />}
        title={product.name}
        description={
          <span className="flex flex-wrap items-center gap-1.5">
            {roles.map((role) => (
              <Badge key={role} tone={role === 'sellable' ? 'primary' : 'neutral'}>
                {ROLE_LABEL[role]}
              </Badge>
            ))}
            {product.archived_at && (
              <Badge tone="outline">
                <Archive aria-hidden />
                Archived <AccountDate value={product.archived_at} />
              </Badge>
            )}
            {product.skus[0] && (
              <span className="ml-1 font-mono text-sm text-muted">{product.skus[0]}</span>
            )}
          </span>
        }
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link to="/stock/$productId" params={{ productId: product.id }}>
                <Boxes aria-hidden />
                See stock
              </Link>
            </Button>
            {canEdit && !product.archived_at && (
              <Button icon={<Pencil aria-hidden />} onClick={() => setEditing(true)}>
                Edit…
              </Button>
            )}
          </>
        }
      />
      {editing && <ProductEditDialog product={product} onClose={() => setEditing(false)} />}
      {product.archived_at && (
        <div
          role="note"
          className="mb-6 flex items-start gap-2.5 rounded-md border border-border-strong bg-surface-sunken px-4 py-3 text-body text-fg"
        >
          <Archive aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
          <p>
            This product is archived: it’s kept for history and reports, but it isn’t offered,
            planned or ordered any more.
          </p>
        </div>
      )}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_19rem] 2xl:grid-cols-[minmax(0,1fr)_22rem] xl:grid-rows-[auto_1fr]">
        {/* Wide: main column left, facts on the right. One column (tablet): the facts
            first, then the recipe and the rest, allergens and family last. */}
        <div className="flex min-w-0 flex-col gap-6 xl:col-start-1 xl:row-span-2 xl:row-start-1">
          <Recipes product={product} />
          <StateRule product={product} />
          <ChannelMappings product={product} />
          <Identifiers product={product} />
        </div>
        <div className="order-first min-w-0 xl:order-none xl:col-start-2 xl:row-start-1">
          <Details product={product} />
        </div>
        <div className="flex min-w-0 flex-col gap-6 xl:col-start-2 xl:row-start-2">
          <Allergens
            product={product}
            onEdit={canEdit && !product.archived_at ? () => setEditing(true) : undefined}
          />
          <Family product={product} />
        </div>
      </div>
    </>
  );
}

function ProductSkeleton() {
  return (
    <div role="status" aria-live="polite">
      <h1 className="sr-only">Loading product</h1>
      <span className="sr-only">Loading product…</span>
      <div className="flex flex-col gap-2 pb-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-5 w-56" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_19rem] 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-col gap-6">
          <Skeleton className="h-72 rounded-lg" />
          <Skeleton className="h-48 rounded-lg" />
        </div>
        <div className="flex flex-col gap-6">
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function BackButton({ icon }: { icon?: LucideIcon }) {
  const Icon = icon ?? ArrowLeft;
  return (
    <Button variant="secondary" asChild>
      <Link to="/catalogue">
        <Icon aria-hidden />
        Back to the catalogue
      </Link>
    </Button>
  );
}

function ProductError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="max-w-2xl">
      <ErrorPanel
        error={error}
        what="This product"
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
        extraAction={<BackButton />}
      />
    </div>
  );
}

function ProductNotFound() {
  useDocumentTitle('Product not found');
  return (
    <div className="max-w-2xl">
      <EmptyState
        icon={PackageSearch}
        titleAs="h1"
        title="Product not found"
        description="There’s no product at this address. It may be in another account, or the link may be mistyped."
        action={<BackButton />}
      />
    </div>
  );
}
